import type { Socket } from 'socket.io-client';
import { fetchIceServers } from './calls-api';

export type CallKind = 'audio' | 'video';
export type CallDirection = 'incoming' | 'outgoing';
export type CallState = 'idle' | 'ringing' | 'connecting' | 'connected' | 'ended';

export interface CallManagerCallbacks {
  onStateChange(state: CallState): void;
  onRemoteStream(stream: MediaStream): void;
  onLocalStream(stream: MediaStream): void;
  onError(err: Error): void;
}

export interface StartCallArgs {
  callId: string;
  kind: CallKind;
  localUserId: string;
  localUserName: string;
  remoteUserId: string;
  conversationId?: string;
}

export interface AcceptCallArgs {
  callId: string;
  kind: CallKind;
  localUserId: string;
  remoteUserId: string;
}

/**
 * Thin wrapper around RTCPeerConnection + socket signaling. One instance
 * per active call — create on start/accept, dispose on hangup.
 *
 * Signaling protocol matches the desktop `CallModal` and the server
 * `ImGateway` events, so desktop ↔ mobile calls route identically.
 */
export class CallManager {
  private pc: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private listeners: Array<{ event: string; handler: (...args: unknown[]) => void }> = [];
  private callId = '';
  private localUserId = '';
  private remoteUserId = '';
  private state: CallState = 'idle';

  constructor(
    private readonly socket: Socket,
    private readonly cb: CallManagerCallbacks,
  ) {}

  private setState(s: CallState) {
    this.state = s;
    this.cb.onStateChange(s);
  }

  private async getIce(): Promise<RTCConfiguration> {
    try {
      const cfg = await fetchIceServers();
      const out: RTCConfiguration = { iceServers: cfg.iceServers };
      if (cfg.iceTransportPolicy) out.iceTransportPolicy = cfg.iceTransportPolicy;
      return out;
    } catch {
      // Fallback to public STUN only if backend is unreachable
      return { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
    }
  }

  private async getLocalMedia(kind: CallKind): Promise<MediaStream> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: kind === 'video',
    });
    this.localStream = stream;
    this.cb.onLocalStream(stream);
    return stream;
  }

  private attachSocketHandlers() {
    const onOffer = async (d: { callId: string; sdp: RTCSessionDescriptionInit }) => {
      if (d.callId !== this.callId || !this.pc) return;
      await this.pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      this.socket.emit('call:answer', {
        callId: this.callId,
        from: this.localUserId,
        to: this.remoteUserId,
        sdp: answer,
      });
    };
    const onAnswer = async (d: { callId: string; sdp: RTCSessionDescriptionInit }) => {
      if (d.callId !== this.callId || !this.pc) return;
      await this.pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
      this.setState('connecting');
    };
    const onIce = async (d: { callId: string; candidate: RTCIceCandidateInit }) => {
      if (d.callId !== this.callId || !this.pc) return;
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(d.candidate));
      } catch {
        /* racy candidates before remote desc — ignore */
      }
    };
    const onEnded = (d: { callId: string }) => {
      if (d.callId === this.callId) this.hangup(false);
    };
    const onRejected = (d: { callId: string }) => {
      if (d.callId === this.callId) this.hangup(false);
    };
    const onAccepted = (d: { callId: string }) => {
      if (d.callId === this.callId) this.setState('connecting');
    };

    this.socket.on('call:offer', onOffer);
    this.socket.on('call:answer', onAnswer);
    this.socket.on('call:ice-candidate', onIce);
    this.socket.on('call:ended', onEnded);
    this.socket.on('call:rejected', onRejected);
    this.socket.on('call:accepted', onAccepted);

    this.listeners = [
      { event: 'call:offer', handler: onOffer as (...a: unknown[]) => void },
      { event: 'call:answer', handler: onAnswer as (...a: unknown[]) => void },
      { event: 'call:ice-candidate', handler: onIce as (...a: unknown[]) => void },
      { event: 'call:ended', handler: onEnded as (...a: unknown[]) => void },
      { event: 'call:rejected', handler: onRejected as (...a: unknown[]) => void },
      { event: 'call:accepted', handler: onAccepted as (...a: unknown[]) => void },
    ];
  }

  private async setupPeer(kind: CallKind): Promise<RTCPeerConnection> {
    const pc = new RTCPeerConnection(await this.getIce());
    this.pc = pc;

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.socket.emit('call:ice-candidate', {
          callId: this.callId,
          from: this.localUserId,
          to: this.remoteUserId,
          candidate: e.candidate.toJSON(),
        });
      }
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (stream) this.cb.onRemoteStream(stream);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') this.setState('connected');
      else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.cb.onError(new Error(`Peer ${pc.connectionState}`));
        this.hangup(true);
      }
    };

    const stream = await this.getLocalMedia(kind);
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    return pc;
  }

  async startOutgoing(args: StartCallArgs) {
    this.callId = args.callId;
    this.localUserId = args.localUserId;
    this.remoteUserId = args.remoteUserId;
    this.setState('ringing');
    this.attachSocketHandlers();

    const pc = await this.setupPeer(args.kind);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Request rings the callee; SDP offer is what the callee negotiates against
    this.socket.emit('call:request', {
      callId: args.callId,
      from: args.localUserId,
      fromName: args.localUserName,
      to: args.remoteUserId,
      callType: args.kind,
      ...(args.conversationId ? { conversationId: args.conversationId } : {}),
    });
    this.socket.emit('call:offer', {
      callId: args.callId,
      from: args.localUserId,
      to: args.remoteUserId,
      sdp: offer,
    });
  }

  async accept(args: AcceptCallArgs) {
    this.callId = args.callId;
    this.localUserId = args.localUserId;
    this.remoteUserId = args.remoteUserId;
    this.setState('connecting');
    this.attachSocketHandlers();

    await this.setupPeer(args.kind);
    this.socket.emit('call:accept', {
      callId: args.callId,
      from: args.localUserId,
      to: args.remoteUserId,
    });
  }

  reject(callId: string, localUserId: string, remoteUserId: string) {
    this.socket.emit('call:reject', {
      callId,
      from: localUserId,
      to: remoteUserId,
      reason: 'declined',
    });
  }

  /**
   * Tear down the peer + release mic/camera. If `notify` is true, also
   * broadcast call:end so the other side stops immediately; pass false
   * when reacting to their hangup (avoid echo).
   */
  hangup(notify = true) {
    if (notify && this.callId) {
      this.socket.emit('call:end', {
        callId: this.callId,
        from: this.localUserId,
        to: this.remoteUserId,
      });
    }
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.pc?.close();
    this.pc = null;
    for (const l of this.listeners) this.socket.off(l.event, l.handler);
    this.listeners = [];
    if (this.state !== 'ended') this.setState('ended');
  }

  toggleMute(mute: boolean) {
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = !mute));
  }

  toggleVideo(off: boolean) {
    this.localStream?.getVideoTracks().forEach((t) => (t.enabled = !off));
  }
}
